import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { invoiceDetailsSelector } from "../../../../slices/invoice/invoiceSlice";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import UserState from "../../../../slices/user/UserState";
import { userSelector } from "../../../../slices/user/userSlice";
import { isInvoicePaymentAlreadyBlocked, startPaymentFlow } from "../../../../slices/payment/paymentSlice";
import { InvoiceDetailsPath, PathParams } from "../route";
import XGSIcons from "../../../../ui-components/icon/xgsIcons";
import Amount from "../../../../ui-components/molecules/amount/amount";
import HeaderStatus from "../../../../ui-components/molecules/header-status/headerStatus";
import ContentContainerHeader from "../../../../ui-components/molecules/content-container-header/contentContainerHeader";
import DownloadPDF from "../../../download-invoice-pdf/downloadPDF";
import styles from "./../../../../sass/variables.module.scss";
import Banner, { BannerColor } from "ui-components/banner/banner";
import { usePaymentBlocked } from "features/shipments/shipment-details/hooks/usePaymentBlocked";
import { goToPaymentStatus } from "../../../../app/data/payment/paymentUtils";
import PendingPaymentNotice from "../../invoice-payment/pendingPaymentNotice";
import PaymentService from "../../../../app/data/payment/paymentService";
import XGSIcon from "../../../../ui-components/icon/xgsIcon";
import ShipmentPaymentHistorySidebar from "../../../shipments/shipment-details/shipmentPaymentHistorySidebar";
import { UserUtils } from "../../../../app/data/user/userUtils";

const InvoiceDetailsHeader: React.FC<{}> = () => {
  const userState: UserState = useSelector(userSelector);
  const urlParams = useParams() as PathParams;
  const dispatch = useDispatch();
  const history = useHistory();
  const { invoiceDetails, ...invoiceDetailsState } = useSelector(invoiceDetailsSelector);

  const urlInvoiceNumber = useMemo(() => {
    return parseInt(urlParams[InvoiceDetailsPath.invoiceNumber], 10);
  }, [urlParams]);

  const [isPaymentBlocked] = usePaymentBlocked(urlInvoiceNumber.toString());
  const pendingPayment = invoiceDetails?.invoice?.pendingPayment;
  const isPending = !!pendingPayment;
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [hasPaymentHistory, setHasPaymentHistory] = useState(false);
  const canOpenPaymentBrowser = UserUtils.canAccessFinancePayments(userState.profile);
  const payorAccountNumber =
    invoiceDetails?.payor?.number || userState.activeSubAccount?.accountNumber;

  useEffect(() => {
    if (!urlInvoiceNumber) {
      setHasPaymentHistory(false);
      return;
    }
    const request = canOpenPaymentBrowser
      ? PaymentService.getInstance().adminSearchPayments({
          invoiceNumber: urlInvoiceNumber,
          page: 0,
          size: 1,
        })
      : payorAccountNumber
      ? PaymentService.getInstance().listPayments({
          accountNumber: payorAccountNumber,
          invoiceNumber: urlInvoiceNumber,
          page: 0,
          size: 1,
        })
      : null;
    if (!request) {
      setHasPaymentHistory(false);
      return;
    }
    request.then((res) => {
      setHasPaymentHistory(!!(res.ok() && res.data && (res.data.totalElements || 0) > 0));
    });
  }, [canOpenPaymentBrowser, payorAccountNumber, urlInvoiceNumber]);

  const pay = async () => {
    if (userState.activeSubAccount && invoiceDetails?.invoice) {
      dispatch(
        startPaymentFlow({
          invoiceNumbers: [invoiceDetails.invoice.invoiceNumber],
          paymentSum: invoiceDetails.invoice.openAmount,
          initiator: "details",
          pendingPaymentId: pendingPayment?.paymentId,
        })
      );
    }
  };

  const isPaid = () =>
    invoiceDetails && invoiceDetails.invoice && invoiceDetails.invoice.paid;

  const isInvoiceDateCurrent = invoiceDetails?.invoice?.dateInvoice
    ? moment(invoiceDetails.invoice.dateInvoice).isSame(moment(), "day")
    : false;

  const showPayButton = () =>
    !!(
      !isPaid() &&
      invoiceDetails?.invoice?.openAmount &&
      invoiceDetails.invoice.openAmount > 0 &&
      !isInvoiceDateCurrent
    );

  const payDisabled =
    isPaymentBlocked ||
    isInvoicePaymentAlreadyBlocked(urlInvoiceNumber.toString()) ||
    isPending;

  return (
    <ContentContainerHeader
      title={`Invoice ${urlInvoiceNumber}`}
      isLoading={invoiceDetailsState.loading}
      className="xgs-invoice-details__header"
    >
      {invoiceDetailsState.loaded && (
        <>
          <div className="xgs-site__content-container-header__bar__column xgs-site__content-container-header__column xgs-invoice-details__header__status">
            <HeaderStatus
              label={isPaid() ? "Invoice Paid" : "Open amount"}
              showIcon={isPaid()}
              icon={XGSIcons.faCheckCircle}
              iconColor={styles.green1}
            >
              <Amount amount={invoiceDetails?.invoice.openAmount} />
            </HeaderStatus>
            {showPayButton() && (
              <Button theme={ButtonThemes.blue} disabled={payDisabled} onClick={pay}>
                Pay Invoice
              </Button>
            )}
          </div>
          <div className="xgs-site__content-container-header__bar__column xgs-site__content-container-header__column">
            &nbsp;
          </div>
          <div className="xgs-site__content-container-header__bar__float-buttons">
            <DownloadPDF invoiceNumbers={urlInvoiceNumber} type="single" />
            {hasPaymentHistory && (
              <div
                className="xgs-invoice-details__payments-button"
                onClick={() => setShowPaymentHistory(true)}
              >
                <div className="xgs-invoice-details__payments-button__icon">
                  <XGSIcon icon={XGSIcons.faCreditCard} />
                </div>
                <div className="xgs-invoice-details__payments-button__label">Payments</div>
              </div>
            )}
          </div>
          {isPending && (
            <PendingPaymentNotice
              onCheckStatus={() => goToPaymentStatus(history, pendingPayment!.paymentId)}
            />
          )}
          {!isPaid() &&
            !isPending &&
            (isPaymentBlocked ||
              isInvoicePaymentAlreadyBlocked(urlInvoiceNumber.toString())) && (
              <div style={{ marginTop: "10px" }}>
                <Banner mods={{ color: BannerColor.red }} mix="xgs-shipment-details__appointment-banner">
                  <div className="xgs-shipment-details__appointment-banner__content">
                    Your payment session expired. Please contact service desk at (844) 947-7447 for
                    assistance before retrying.
                  </div>
                </Banner>
              </div>
            )}
        </>
      )}
      <ShipmentPaymentHistorySidebar
        show={showPaymentHistory}
        onClose={() => setShowPaymentHistory(false)}
        invoiceNumber={urlInvoiceNumber}
        accountNumber={payorAccountNumber}
        canOpenPaymentBrowser={canOpenPaymentBrowser}
      />
    </ContentContainerHeader>
  );
};

export default InvoiceDetailsHeader;
